import type { ShopProductType } from '@kanbu/schema';
import {
  ProductAvailabilityStatus,
  type ShopProductStatus,
} from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import {
  createFileRoute,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { Badge, Button, Input, Select, SelectItem } from '@utima/ui';
import { DatabaseBackup, EyeOff, Settings, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ConfirmDialog } from '@/components/confirmDialog/ConfirmDialog';
import type { DataTableQuery } from '@/components/dataTable/DataTable';
import { SimpleTooltip } from '@/components/index';
import { TablePage } from '@/components/page/TablePage';
import {
  FeedImportProgress,
  getFeedImportCounts,
} from '@/features/feeds/FeedImportProgress';
import { FeedSettingsModal } from '@/features/feeds/FeedSettingsModal';
import { ProductDetailModal } from '@/features/shopProducts/components/ProductDetailModal';
import { ProductStatsSummary } from '@/features/shopProducts/components/ProductStatsSummary';
import { useShopProductMutation } from '@/features/shopProducts/useShopProductMutation';
import { useShopProductsCols } from '@/features/shopProducts/useShopProductsCols';
import { useEnumTranslations } from '@/hooks';
import { trpc, trpcClient } from '@/services/trpc';

type ProductsSearch = {
  status?: ShopProductStatus;
  page?: number;
  pageSize?: number;
  sort?: string;
  search?: string;
};

const ALL = '__all__';

// Mirrors KBToolbar's `toolbarShellClassName`: one shell shared by the
// default (search + filters + settings) and selection states so swapping
// between them never changes the toolbar's height.
const toolbarShellClassName =
  'mb-4 flex min-h-11 items-center justify-between gap-2 rounded-lg border py-1.5 px-2';

const FEED_PRODUCT_COUNTS_POPULATE = [
  'pendingProductsCount',
  'embeddingProductsCount',
  'doneProductsCount',
  'failedProductsCount',
] as const;

export const Route = createFileRoute(
  '/_baseLayout/chats/$chatId/feeds/$feedId/products',
)({
  component: ProductsPage,
  loader: async ({ context: { trpcUtils }, params: { feedId, chatId } }) =>
    Promise.all([
      trpcUtils.feeds.findOne.ensureData({
        id: feedId,
        populate: [...FEED_PRODUCT_COUNTS_POPULATE],
      }),
      trpcUtils.chats.findOne.ensureData({ id: chatId }),
    ]),
  validateSearch: (
    search: ProductsSearch & SearchSchemaInput,
  ): ProductsSearch => ({
    // `status` is the deep-linked filter; page/pageSize/sort/search are the
    // DataTable's own params and must be preserved here or the table loses
    // pagination/sort/search on this route.
    ...(search?.status ? { status: search.status } : {}),
    ...(search?.page === undefined ? {} : { page: search.page }),
    ...(search?.pageSize === undefined ? {} : { pageSize: search.pageSize }),
    ...(search?.sort ? { sort: search.sort } : {}),
    ...(search?.search ? { search: search.search } : {}),
  }),
});

function ProductsPage() {
  const { chatId, feedId } = Route.useParams();
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();

  const [chatData] = trpc.chats.findOne.useSuspenseQuery({ id: chatId });
  const [feedData] = trpc.feeds.findOne.useSuspenseQuery(
    {
      id: feedId,
      populate: [...FEED_PRODUCT_COUNTS_POPULATE],
    },
    { refetchInterval: 5000 },
  );

  // Status is URL-driven so feeds-page deep links (`?status=`) prefilter the
  // table and the stats bar highlights the matching row; the other filters
  // stay local since they aren't deep-linked.
  const statusFilter = searchParams?.status;

  const handleStatusFilterChange = useCallback(
    (next: ShopProductStatus | undefined) => {
      navigate({
        search: prev => ({ ...prev, status: next, page: 1 }),
      });
    },
    [navigate],
  );

  const [availabilityFilter, setAvailabilityFilter] = useState<
    ProductAvailabilityStatus | undefined
  >();
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>();
  const [brandFilter, setBrandFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const hasSelection = selectedIds.size > 0;
  const [detailProductId, setDetailProductId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reEmbedConfirmOpen, setReEmbedConfirmOpen] = useState(false);

  const { reEmbed, update } = useShopProductMutation();

  const { found, awaiting } = getFeedImportCounts(feedData);
  const pendingCount = Number(feedData.pendingProductsCount);
  const embeddingCount = Number(feedData.embeddingProductsCount);
  const doneCount = Number(feedData.doneProductsCount);
  const failedCount = Number(feedData.failedProductsCount);

  const handleToggleOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);

      if (checked) next.add(id);
      else next.delete(id);

      return next;
    });
  }, []);

  const handleToggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);

      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }

      return next;
    });
  }, []);

  const columns = useShopProductsCols({
    onOpenDetail: setDetailProductId,
  });

  const tableMeta = useMemo(
    () => ({
      selectedIds,
      onToggleOne: handleToggleOne,
      onToggleAll: handleToggleAll,
    }),
    [selectedIds, handleToggleOne, handleToggleAll],
  );

  const handleQuery = useCallback<DataTableQuery<ShopProductType>>(
    async ({ page, pageSize, sort }) => {
      const where: Record<string, unknown> = { feed: feedId };

      if (debouncedSearch) where.name = { $ilike: `%${debouncedSearch}%` };
      if (statusFilter) where.status = statusFilter;
      if (availabilityFilter) where.availability = availabilityFilter;
      if (activeFilter !== undefined) where.active = activeFilter;

      if (brandFilter) {
        where.$or = [
          { brand: { $ilike: `%${brandFilter}%` } },
          { categories: { name: { $ilike: `%${brandFilter}%` } } },
          { categories: { displayName: { $ilike: `%${brandFilter}%` } } },
        ];
      }

      const data = await trpcClient.shopProducts.findAll.query({
        where,
        pagination: { page, pageSize },
        orderBy: sort,
        populate: ['categories'],
      });

      return {
        items: data.items as unknown as ShopProductType[],
        pageCount: data.meta.pageCount,
      };
    },
    [
      feedId,
      debouncedSearch,
      statusFilter,
      availabilityFilter,
      activeFilter,
      brandFilter,
    ],
  );

  const handleBulkReEmbed = useCallback(async () => {
    await Promise.all([...selectedIds].map(id => reEmbed.mutateAsync({ id })));
    setSelectedIds(new Set());
  }, [selectedIds, reEmbed]);

  const handleBulkDeactivate = useCallback(async () => {
    await Promise.all(
      [...selectedIds].map(id => update.mutateAsync({ id, active: false })),
    );
    setSelectedIds(new Set());
  }, [selectedIds, update]);

  return (
    <>
      <TablePage
        refetchInterval={5000}
        title={feedData.name}
        breadcrumbs={[
          {
            label: chatData.name,
            to: '/chats/$chatId/edit',
            params: { chatId },
          },
          {
            label: t`Product feeds`,
            to: '/chats/$chatId/feeds',
            params: { chatId },
          },
          {
            label: feedData.name,
            to: '/chats/$chatId/feeds/$feedId/products',
            params: { chatId, feedId },
          },
        ]}
        columns={columns}
        showSearch={false}
        options={{ meta: tableMeta }}
        queryKey={[
          ...getQueryKey(trpc.shopProducts.findAll),
          {
            feedId,
            debouncedSearch,
            statusFilter,
            availabilityFilter,
            activeFilter,
            brandFilter,
          },
        ]}
        onQueryFn={handleQuery}
        onRow={(_event, row) =>
          handleToggleOne(row.original.id, !selectedIds.has(row.original.id))
        }
        onDoubleClick={(_event, row) => setDetailProductId(row.original.id)}
      >
        <FeedImportProgress feed={feedData} showCounts={false} />
        <ProductStatsSummary
          foundCount={found}
          awaitingImportCount={awaiting}
          pendingCount={pendingCount}
          embeddingCount={embeddingCount}
          doneCount={doneCount}
          failedCount={failedCount}
          activeStatusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
        />

        <div
          className={
            hasSelection
              ? `${toolbarShellClassName} border border-primary/20 bg-primary/5`
              : `${toolbarShellClassName} border-transparent`
          }
        >
          {hasSelection ? (
            <>
              <div className='flex items-center gap-3'>
                <span className='text-sm font-medium'>
                  {t`${selectedIds.size} selected`}
                </span>
                <button
                  className='text-xs text-muted-fg underline'
                  onClick={() => setSelectedIds(new Set())}
                >
                  {t`Deselect`}
                </button>
              </div>

              <div className='flex items-center gap-1'>
                <SimpleTooltip title={t`Re-embed selected`}>
                  <Button
                    variant='ghost'
                    outline
                    size='icon-xs'
                    onClick={() => setReEmbedConfirmOpen(true)}
                    aria-label={t`Re-embed selected`}
                  >
                    <DatabaseBackup className='size-4' />
                  </Button>
                </SimpleTooltip>
                <SimpleTooltip title={t`Deactivate selected`}>
                  <Button
                    variant='ghost'
                    outline
                    size='icon-xs'
                    className='text-danger'
                    loading={update.isPending}
                    onClick={handleBulkDeactivate}
                    aria-label={t`Deactivate selected`}
                  >
                    <EyeOff className='size-4' />
                  </Button>
                </SimpleTooltip>
              </div>
            </>
          ) : (
            <>
              <div className='flex flex-wrap items-center gap-2'>
                <div className='w-48 shrink-0'>
                  <Input
                    type='search'
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={t`Search`}
                    className='h-8'
                  />
                </div>

                <div className='w-44 shrink-0'>
                  <Select
                    size='sm'
                    value={availabilityFilter ?? ALL}
                    onValueChange={value =>
                      setAvailabilityFilter(
                        value === ALL
                          ? undefined
                          : (value as ProductAvailabilityStatus),
                      )
                    }
                    placeholder={t`Availability`}
                  >
                    <SelectItem value={ALL}>{t`All availability`}</SelectItem>
                    {Object.values(ProductAvailabilityStatus).map(
                      availability => (
                        <SelectItem key={availability} value={availability}>
                          {
                            enumTranslations.productAvailabilityStatus[
                              availability
                            ]
                          }
                        </SelectItem>
                      ),
                    )}
                  </Select>
                </div>

                <div className='w-44 shrink-0'>
                  <Select
                    size='sm'
                    value={
                      activeFilter === undefined ? ALL : String(activeFilter)
                    }
                    onValueChange={value =>
                      setActiveFilter(
                        value === ALL ? undefined : value === 'true',
                      )
                    }
                    placeholder={t`Active`}
                  >
                    <SelectItem value={ALL}>{t`Active + inactive`}</SelectItem>
                    <SelectItem value='true'>{t`Active only`}</SelectItem>
                    <SelectItem value='false'>{t`Inactive only`}</SelectItem>
                  </Select>
                </div>

                <div className='w-40 shrink-0'>
                  <Input
                    size='sm'
                    value={brandFilter}
                    onChange={event => setBrandFilter(event.target.value)}
                    placeholder={t`Filter by brand or category`}
                  />
                </div>

                {statusFilter && (
                  <Badge variant='secondary' size='sm' className='gap-1 pr-1'>
                    {t`Status: ${enumTranslations.shopProductStatus[statusFilter]}`}
                    <button
                      type='button'
                      className='rounded p-0.5 hover:bg-muted'
                      aria-label={t`Clear filter`}
                      onClick={() => handleStatusFilterChange(undefined)}
                    >
                      <X className='size-3' />
                    </button>
                  </Badge>
                )}
              </div>

              <Button
                data-cta='admin-feed-settings-button'
                variant='ghost'
                outline
                size='sm'
                className='shrink-0'
                onClick={() => setSettingsOpen(true)}
              >
                <Settings className='size-4' /> {t`Settings`}
              </Button>
            </>
          )}
        </div>
      </TablePage>

      <ConfirmDialog
        open={reEmbedConfirmOpen}
        onOpenChange={setReEmbedConfirmOpen}
        onConfirm={handleBulkReEmbed}
        title={t`Re-embed ${selectedIds.size} selected product(s)?`}
        description={t`Selected products will be re-queued for embedding using their current data.`}
        confirmLabel={t`Re-embed`}
        confirmVariant='primary'
        successTitle={t`Products queued for re-embedding`}
        successDescription={t`They will be re-embedded shortly.`}
      />

      <ProductDetailModal
        productId={detailProductId}
        onClose={() => setDetailProductId(null)}
        chatId={chatId}
        feedId={feedId}
      />

      <FeedSettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        chatId={chatId}
        feedId={feedId}
      />
    </>
  );
}
